import { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Container, ModalContainer } from '../BaseModal';
import { BodyLarge, TitleLarge } from '../Texts';
import LoadingSpinner from '../LoadingSpinner';
import Button from '../Button';

import { selectPrivyToken } from '../../redux/slices/auth';
import { useGetWithdrawalInfoQuery } from '../../redux/api/authorized/withdrawal';
import { toTokenAmount, useSolana } from '../../hooks/useSolana';
import { UPDATE_BALANCE } from '../../common/constants';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 16px;
`;

const Swap = () => {
  const privyToken = useSelector(selectPrivyToken);
  const navigate = useNavigate();
  const theme = useTheme();
  const { type, id } = useParams();

  const { data: withdrawalInfo } = useGetWithdrawalInfoQuery(id, {
    skip: !id || type !== 'withdraw' || !privyToken,
  });

  const {
    sendUsdc,
    walletsReady,
    loading,
    getWalletPendingTransactions,
  } = useSolana();

  const [pendingTransactions, setPendingTransactions] = useState([]);

  // Load pending transactions to disable confirm if something is already in-flight
  useEffect(() => {
    (async () => {
      try {
        const txs = await getWalletPendingTransactions();
        setPendingTransactions(txs || []);
      } catch {
        setPendingTransactions([]);
      }
    })();
  }, [getWalletPendingTransactions]);

  // If withdrawal is not in "started" state anymore, redirect back to wallet
  useEffect(() => {
    const checkWithdrawalStatus = async () => {
      if (withdrawalInfo && walletsReady) {
        if (withdrawalInfo.status !== 'started') {
          navigate('/app/wallet');
        }
      }
    };

    checkWithdrawalStatus();
  }, [withdrawalInfo, walletsReady, navigate]);

  // For deposits, just wait and then notify parent window to refresh balances
  useEffect(() => {
    const refreshBalances = () => {
      setTimeout(() => {
        if (window.opener) {
          window.opener.postMessage(
            { type: UPDATE_BALANCE },
            import.meta.env.VITE_FE_HOST,
          );
          window.close();
        } else {
          navigate('/');
        }
      }, 5000);
    };

    if (type === 'deposit') {
      refreshBalances();
    }
  }, [type, navigate]);

  if (withdrawalInfo?.status === 'started' && !loading) {
    return (
      <Container>
        <ModalContainer>
          <TitleLarge>Confirm withdraw</TitleLarge>
          <BodyLarge style={{ color: theme.text.secondary }}>
            When clicking confirm, you will transfer $
            {withdrawalInfo.outputAmount} USDC to Coinbase and complete the
            withdraw process.
          </BodyLarge>
          <Button
            disabled={pendingTransactions?.length !== 0}
            onClick={async () => {
              if (withdrawalInfo && walletsReady) {
                const resp = await sendUsdc(
                  toTokenAmount(withdrawalInfo.outputAmount, 6),
                  withdrawalInfo.addressTo,
                );

                if (resp?.success) {
                  if (window.opener) {
                    window.opener.postMessage(
                      { type: UPDATE_BALANCE },
                      import.meta.env.VITE_FE_HOST,
                    );
                    window.close();
                  } else {
                    navigate('/');
                  }
                } else {
                  toast('Withdrawal failed', { type: 'error' });
                }
              }
            }}
          >
            Confirm
          </Button>
        </ModalContainer>
      </Container>
    );
  }

  return (
    <Container>
      <ModalContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <BodyLarge>Completing {type}...</BodyLarge>
        </LoadingContainer>
      </ModalContainer>
    </Container>
  );
};

export default Swap;
